/**
 * Maintenance StatusBadge
 *
 * Wraps the shared UI StatusBadge using the "maintenance" type from designSystem.ts
 * and prepends a contextual icon that is specific to the maintenance module.
 *
 * Status strings accepted (case-insensitive):
 *   OPEN, PENDING, FOLLOW_UP, CLOSED, CANCELLED
 */

import React from 'react';
import {
  RiTimeLine,
  RiAlertLine,
  RiCloseCircleLine,
  RiRadioButtonLine,
  RiLockLine,
} from 'react-icons/ri';
import { cn } from '../../utils/cn';
import { statusColors } from '../../config/designSystem';

// ---------------------------------------------------------------------------
// Type & Icons
// ---------------------------------------------------------------------------
type MaintenanceKey = keyof typeof statusColors.maintenance;

const ICONS: Record<MaintenanceKey, React.ReactElement> = {
  OPEN:          <RiRadioButtonLine className="h-3.5 w-3.5 flex-shrink-0" />,
  PENDING:       <RiTimeLine className="h-3.5 w-3.5 flex-shrink-0" />,
  FOLLOW_UP:     <RiAlertLine className="h-3.5 w-3.5 flex-shrink-0" />,
  CLOSED:        <RiLockLine className="h-3.5 w-3.5 flex-shrink-0" />,
  CANCELLED:     <RiCloseCircleLine className="h-3.5 w-3.5 flex-shrink-0" />,
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
interface StatusBadgeProps {
  status: string;
  className?: string;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ status, className = '' }) => {
  const key = (status.toUpperCase() in statusColors.maintenance
    ? status.toUpperCase()
    : 'OPEN') as MaintenanceKey;
  const config = statusColors.maintenance[key];
  const icon = ICONS[key];

  // The shared StatusBadge does not support arbitrary React node children as
  // icons, so we compose the badge shell directly from designSystem tokens
  // (same source of truth) while adding the module-specific icon.
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border',
        config.bg,
        config.text,
        config.border,
        className
      )}
    >
      {icon}
      <span>{config.label}</span>
    </span>
  );
};

export default StatusBadge;
